import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { tcApi, outsourcingApi } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import {
  CalendarRange, Plus, Building, Users, CheckCircle2, XCircle, Loader2,
  GraduationCap, UtensilsCrossed, BedDouble, Ban, Hotel,
} from 'lucide-react';

const STATUS_STYLES = {
  TENTATIVE: 'bg-amber-100 text-amber-700',
  CONFIRMED: 'bg-emerald-100 text-emerald-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  COMPLETED: 'bg-slate-100 text-slate-700',
  CANCELLED: 'bg-red-100 text-red-600',
};
const MEAL_PLANS = ['Full Board', 'Half Board', 'Lunch Only', 'Tea Breaks Only'];

export default function VenueBookingPage() {
  const { currentProperty } = useAuth();
  const pid = currentProperty?.id;
  const [tab, setTab] = useState('bookings');
  const [dashboard, setDashboard] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [venueDialog, setVenueDialog] = useState(false);
  const [lodgingFor, setLodgingFor] = useState(null);

  const loadAll = useCallback(async () => {
    if (!pid) return;
    try {
      const [d, b, v] = await Promise.all([
        tcApi.dashboard(pid), tcApi.listBookings(pid), tcApi.listVenues(pid),
      ]);
      setDashboard(d.data); setBookings(b.data || []); setVenues(v.data || []);
    } catch (e) {
      toast.error('Failed to load training center data');
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const doCancel = async (id) => {
    try { await tcApi.cancelBooking(id); toast.success('Booking cancelled'); loadAll(); }
    catch (e) { toast.error('Failed to cancel'); }
  };

  if (!currentProperty) {
    return <div className="p-6"><Card><CardContent className="p-8 text-center text-slate-500">Select a property.</CardContent></Card></div>;
  }

  const dv = dashboard?.venues || {};
  const db = dashboard?.bookings || {};

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-primary text-white flex items-center justify-center">
            <CalendarRange className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Venue Bookings</h1>
            <p className="text-sm text-slate-500">Book a training venue first, then add lodging, catering &amp; training</p>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 flex items-center gap-3"><Building className="h-5 w-5 text-slate-500" /><div><p className="text-xs text-slate-500">Venues</p><p className="text-xl font-semibold">{dv.total || 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><CheckCircle2 className="h-5 w-5 text-emerald-500" /><div><p className="text-xs text-slate-500">Free Today</p><p className="text-xl font-semibold">{dv.free_today ?? 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><CalendarRange className="h-5 w-5 text-blue-500" /><div><p className="text-xs text-slate-500">Active Bookings</p><p className="text-xl font-semibold">{db.active || 0}</p></div></CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3"><Users className="h-5 w-5 text-amber-500" /><div><p className="text-xs text-slate-500">Tentative</p><p className="text-xl font-semibold">{db.tentative || 0}</p></div></CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="bookings">Bookings</TabsTrigger>
          <TabsTrigger value="venues">Venues</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setBookingDialog(true)}><Plus className="h-4 w-4 mr-2" /> New Booking</Button>
          </div>
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Booking</TableHead><TableHead>Client</TableHead><TableHead>Venue</TableHead>
                    <TableHead>Dates</TableHead><TableHead className="text-right">Pax</TableHead>
                    <TableHead>Services</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></TableCell></TableRow>
                  ) : bookings.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">No bookings yet. Create one to start.</TableCell></TableRow>
                  ) : bookings.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="font-mono text-xs">{b.booking_number}</TableCell>
                      <TableCell><div className="font-medium">{b.client_name}</div><div className="text-xs text-slate-500">{b.company_name}</div></TableCell>
                      <TableCell>{b.function_spaces?.name || '—'}</TableCell>
                      <TableCell className="text-sm">{b.start_date} → {b.end_date}</TableCell>
                      <TableCell className="text-right">{b.pax}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {b.includes_training && <GraduationCap className="h-4 w-4 text-indigo-500" title="Training" />}
                          {b.includes_catering && <UtensilsCrossed className="h-4 w-4 text-orange-500" title="Catering" />}
                          {b.includes_lodging && <BedDouble className="h-4 w-4 text-teal-500" title="Lodging" />}
                        </div>
                      </TableCell>
                      <TableCell><Badge className={STATUS_STYLES[b.status] || ''}>{b.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        {b.includes_lodging && b.status !== 'CANCELLED' && (
                          <Button variant="ghost" size="icon" onClick={() => setLodgingFor(b)} title="Manage lodging"><BedDouble className="h-4 w-4 text-teal-600" /></Button>
                        )}
                        {b.status !== 'CANCELLED' && b.status !== 'COMPLETED' && (
                          <Button variant="ghost" size="icon" onClick={() => doCancel(b.id)} title="Cancel"><Ban className="h-4 w-4 text-red-500" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="venues" className="space-y-4">
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setVenueDialog(true)}><Plus className="h-4 w-4 mr-2" /> Add Venue</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {venues.map((v) => (
              <Card key={v.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-semibold">{v.name}</p>
                      <p className="text-xs text-slate-500 font-mono">{v.code}</p>
                    </div>
                    {!v.is_active && <Badge variant="secondary">Inactive</Badge>}
                  </div>
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                    <Users className="h-4 w-4" /> Capacity: <span className="font-semibold">{v.seating_capacity}</span>
                  </div>
                  {v.location && <p className="text-xs text-slate-500 mt-1">{v.location}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      <BookingDialog open={bookingDialog} onOpenChange={setBookingDialog} propertyId={pid} onSaved={() => { setBookingDialog(false); loadAll(); }} />
      <VenueDialog open={venueDialog} onOpenChange={setVenueDialog} propertyId={pid} onSaved={() => { setVenueDialog(false); loadAll(); }} />
      <LodgingDialog booking={lodgingFor} onClose={() => setLodgingFor(null)} onChanged={loadAll} />
    </div>
  );
}

function BookingDialog({ open, onOpenChange, propertyId, onSaved }) {
  const [form, setForm] = useState({});
  const [availability, setAvailability] = useState([]);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ start_date: '', end_date: '', pax: '', venue_id: '', client_name: '', company_name: '',
        contact_email: '', contact_phone: '', includes_training: false, training_topic: '', trainer: '',
        includes_catering: false, meal_plan: '', includes_lodging: false, rooms_count: '' });
      setAvailability([]);
    }
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const checkAvailability = useCallback(async () => {
    if (!form.start_date || !form.end_date) return;
    try {
      setChecking(true);
      const res = await tcApi.venueAvailability(propertyId, form.start_date, form.end_date, parseInt(form.pax) || 0);
      setAvailability(res.data || []);
    } catch (e) {
      setAvailability([]);
    } finally {
      setChecking(false);
    }
  }, [propertyId, form.start_date, form.end_date, form.pax]);

  useEffect(() => { if (open && form.start_date && form.end_date) checkAvailability(); }, [open, form.start_date, form.end_date, form.pax, checkAvailability]);

  const save = async () => {
    if (!form.client_name?.trim()) { toast.error('Enter client name'); return; }
    if (!form.venue_id) { toast.error('Select an available venue'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Select dates'); return; }
    try {
      setSaving(true);
      await tcApi.createBooking(propertyId, {
        client_name: form.client_name,
        company_name: form.company_name || null,
        contact_email: form.contact_email || null,
        contact_phone: form.contact_phone || null,
        venue_id: form.venue_id,
        start_date: form.start_date,
        end_date: form.end_date,
        pax: parseInt(form.pax) || 0,
        includes_training: !!form.includes_training,
        training_topic: form.training_topic || null,
        trainer: form.trainer || null,
        includes_catering: !!form.includes_catering,
        meal_plan: form.meal_plan || null,
        includes_lodging: !!form.includes_lodging,
        rooms_count: parseInt(form.rooms_count) || 0,
      });
      toast.success('Booking created');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create booking');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New Venue Booking</DialogTitle>
          <DialogDescription>Pick dates &amp; a venue first — availability and capacity are checked automatically.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: dates + pax */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Start date</Label><Input type="date" value={form.start_date || ''} onChange={(e) => set('start_date', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>End date</Label><Input type="date" value={form.end_date || ''} onChange={(e) => set('end_date', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Attendees</Label><Input type="number" value={form.pax || ''} onChange={(e) => set('pax', e.target.value)} placeholder="0" /></div>
          </div>

          {/* Step 2: venue picker (availability-gated) */}
          <div className="space-y-2">
            <Label>Select Venue {checking && <Loader2 className="h-3 w-3 inline animate-spin ml-1" />}</Label>
            {(!form.start_date || !form.end_date) ? (
              <p className="text-xs text-slate-500">Choose dates to see available venues.</p>
            ) : availability.length === 0 ? (
              <p className="text-xs text-slate-500">No venues found.</p>
            ) : (
              <div className="space-y-2">
                {availability.map((v) => {
                  const ok = v.available && v.capacity_ok;
                  return (
                    <button
                      key={v.id} type="button" disabled={!ok}
                      onClick={() => set('venue_id', v.id)}
                      data-testid={`venue-opt-${v.code}`}
                      className={[
                        'w-full flex items-center justify-between rounded-lg border p-3 text-left transition',
                        !ok ? 'opacity-60 cursor-not-allowed bg-slate-50' : 'hover:border-primary',
                        form.venue_id === v.id ? 'border-primary ring-1 ring-primary' : 'border-slate-200',
                      ].join(' ')}
                    >
                      <div>
                        <p className="font-medium text-sm">{v.name} <span className="text-xs text-slate-400 font-mono">{v.code}</span></p>
                        <p className="text-xs text-slate-500">Capacity {v.seating_capacity}</p>
                      </div>
                      {!v.available ? (
                        <Badge className="bg-red-100 text-red-600"><XCircle className="h-3 w-3 mr-1" />Booked</Badge>
                      ) : !v.capacity_ok ? (
                        <Badge className="bg-amber-100 text-amber-700">Too small</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="h-3 w-3 mr-1" />Available</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Step 3: client */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Client name</Label><Input value={form.client_name || ''} onChange={(e) => set('client_name', e.target.value)} placeholder="Organization / person" /></div>
            <div className="space-y-1.5"><Label>Company</Label><Input value={form.company_name || ''} onChange={(e) => set('company_name', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input value={form.contact_email || ''} onChange={(e) => set('contact_email', e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input value={form.contact_phone || ''} onChange={(e) => set('contact_phone', e.target.value)} /></div>
          </div>

          {/* Step 4: services */}
          <div className="space-y-3 rounded-lg border p-3">
            <p className="text-sm font-medium">Services</p>
            <div className="flex items-center gap-2">
              <Checkbox id="trn" checked={!!form.includes_training} onCheckedChange={(c) => set('includes_training', !!c)} />
              <Label htmlFor="trn" className="flex items-center gap-1"><GraduationCap className="h-4 w-4" /> Training</Label>
            </div>
            {form.includes_training && (
              <div className="grid grid-cols-2 gap-3 pl-6">
                <Input placeholder="Topic" value={form.training_topic || ''} onChange={(e) => set('training_topic', e.target.value)} />
                <Input placeholder="Trainer" value={form.trainer || ''} onChange={(e) => set('trainer', e.target.value)} />
              </div>
            )}
            <div className="flex items-center gap-2">
              <Checkbox id="cat" checked={!!form.includes_catering} onCheckedChange={(c) => set('includes_catering', !!c)} />
              <Label htmlFor="cat" className="flex items-center gap-1"><UtensilsCrossed className="h-4 w-4" /> Catering</Label>
            </div>
            {form.includes_catering && (
              <div className="pl-6 flex flex-wrap gap-2">
                {MEAL_PLANS.map((m) => (
                  <button key={m} type="button" onClick={() => set('meal_plan', m)}
                    className={`text-xs px-2 py-1 rounded border ${form.meal_plan === m ? 'border-primary bg-primary/10' : 'border-slate-200'}`}>{m}</button>
                ))}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Checkbox id="lod" checked={!!form.includes_lodging} onCheckedChange={(c) => set('includes_lodging', !!c)} />
              <Label htmlFor="lod" className="flex items-center gap-1"><BedDouble className="h-4 w-4" /> Lodging</Label>
            </div>
            {form.includes_lodging && (
              <div className="pl-6"><Input type="number" placeholder="Rooms needed" value={form.rooms_count || ''} onChange={(e) => set('rooms_count', e.target.value)} className="w-40" /></div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Create Booking</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function VenueDialog({ open, onOpenChange, propertyId, onSaved }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [location, setLocation] = useState('');
  const [cap, setCap] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) { setName(''); setCode(''); setLocation(''); setCap(''); } }, [open]);

  const save = async () => {
    if (!name.trim()) { toast.error('Enter a venue name'); return; }
    try {
      setSaving(true);
      await tcApi.createVenue(propertyId, { name, code: code || null, location: location || null, seating_capacity: parseInt(cap) || 0 });
      toast.success('Venue added');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add venue');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add Training Venue</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Main Hall" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Code</Label><Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="optional" /></div>
            <div className="space-y-1.5"><Label>Capacity</Label><Input type="number" value={cap} onChange={(e) => setCap(e.target.value)} /></div>
          </div>
          <div className="space-y-1.5"><Label>Location</Label><Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Block / floor" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LodgingDialog({ booking, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [partnerId, setPartnerId] = useState('');
  const [rate, setRate] = useState('');

  const load = useCallback(async () => {
    if (!booking) return;
    try {
      setLoading(true);
      const [lr, pr] = await Promise.all([tcApi.bookingLodging(booking.id), outsourcingApi.listPartners(booking.property_id)]);
      setData(lr.data);
      setPartners(pr.data || []);
    } catch (e) { toast.error('Failed to load lodging'); }
    finally { setLoading(false); }
  }, [booking]);

  useEffect(() => { if (booking) { load(); setPartnerId(''); setRate(''); } else setData(null); }, [booking, load]);

  const allocate = async () => {
    try {
      setBusy(true);
      const r = await tcApi.allocateLodging(booking.id);
      toast.success(`Allocated ${r.data.created} room(s)`);
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to allocate'); }
    finally { setBusy(false); }
  };
  const release = async () => {
    try {
      setBusy(true);
      await tcApi.releaseLodging(booking.id);
      toast.success('Rooms released');
      await load(); onChanged && onChanged();
    } catch (e) { toast.error('Failed to release'); }
    finally { setBusy(false); }
  };
  const outsource = async () => {
    if (!partnerId) { toast.error('Select a partner hotel'); return; }
    try {
      setBusy(true);
      await tcApi.outsourceOverflow(booking.id, { partner_hotel_id: partnerId, rate_per_night: parseFloat(rate) || 0, guests_count: 0 });
      toast.success('Overflow outsourced to partner hotel');
      setPartnerId(''); setRate('');
      await load(); onChanged && onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to outsource'); }
    finally { setBusy(false); }
  };

  const needed = data?.rooms_needed || 0;
  const allocated = data?.rooms_allocated || 0;
  const outsourcedRooms = data?.outsourced_rooms || 0;
  const shortfall = data?.shortfall ?? 0;

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Lodging — {booking?.client_name}</DialogTitle>
          <DialogDescription>
            Allocate in-house rooms or outsource overflow to a partner hotel. {data?.dates && `${data.dates.start} → ${data.dates.end}`}
          </DialogDescription>
        </DialogHeader>
        {loading ? <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
          : data && (
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] text-slate-500">Needed</p><p className="text-lg font-semibold">{needed}</p></div>
              <div className="rounded-lg bg-emerald-50 p-3"><p className="text-[11px] text-slate-500">Allocated</p><p className="text-lg font-semibold text-emerald-700">{allocated}</p></div>
              <div className="rounded-lg bg-indigo-50 p-3"><p className="text-[11px] text-slate-500">Outsourced</p><p className="text-lg font-semibold text-indigo-700">{outsourcedRooms}</p></div>
              <div className="rounded-lg bg-amber-50 p-3"><p className="text-[11px] text-slate-500">Short</p><p className="text-lg font-semibold text-amber-700">{shortfall}</p></div>
            </div>

            {data.allocated.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">Allocated Rooms (in-house)</p>
                <div className="flex flex-wrap gap-1.5">
                  {data.allocated.map((a) => (
                    <Badge key={a.id} variant="outline" className="font-mono">{a.rooms?.room_number || '—'}</Badge>
                  ))}
                </div>
              </div>
            )}

            {(data.outsourced || []).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-1">Outsourced to Partner Hotels</p>
                <div className="space-y-1">
                  {data.outsourced.map((o) => (
                    <div key={o.id} className="flex items-center justify-between text-sm rounded border p-2">
                      <span><Hotel className="h-3.5 w-3.5 inline mr-1 text-indigo-500" />{o.partner_hotels?.name || 'Partner'} · {o.rooms_count} room(s)</span>
                      <Badge variant="secondary">{o.status}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* In-house allocation */}
            {shortfall > 0 && data.available_count > 0 && (
              <div className="rounded-lg border p-3 flex items-center justify-between">
                <p className="text-sm">{data.available_count} room(s) available in-house</p>
                <Button size="sm" onClick={allocate} disabled={busy}>{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BedDouble className="h-4 w-4 mr-2" />} Allocate</Button>
              </div>
            )}

            {/* Outsourcing overflow */}
            {shortfall > 0 && (
              <div className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 space-y-2">
                <p className="text-sm font-medium flex items-center gap-1"><Hotel className="h-4 w-4 text-indigo-600" /> Outsource overflow ({shortfall} room{shortfall > 1 ? 's' : ''})</p>
                {partners.length === 0 ? (
                  <p className="text-xs text-slate-500">No partner hotels yet — add one in the Outsourcing page.</p>
                ) : (
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Partner hotel</Label>
                      <select value={partnerId} onChange={(e) => setPartnerId(e.target.value)} className="w-full border rounded h-9 px-2 text-sm">
                        <option value="">Select partner…</option>
                        {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Rate/night</Label>
                      <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="0" className="h-9" />
                    </div>
                    <Button size="sm" variant="outline" onClick={outsource} disabled={busy}>Outsource</Button>
                  </div>
                )}
              </div>
            )}

            {shortfall === 0 && needed > 0 && (
              <p className="text-xs text-emerald-600">All {needed} room(s) covered (allocated + outsourced).</p>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
          {allocated > 0 && <Button variant="outline" onClick={release} disabled={busy}>Release In-House</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

